import classNames from "classnames";
import { useState } from "react";
import { BaseImage } from "../../../components/Content/BaseImage.tsx";
import { ErrorWrapper } from "../../../components/Error/ErrorWrapper.tsx";
import { ClickableIcon } from "../../../components/Icon/ClickableIcon.tsx";
import { Icon } from "../../../components/Icon/Icon.tsx";
import { Spinner } from "../../../components/Spinner/Spinner.tsx";
import { useOnMount } from "../../../hooks/useOnMount.ts";
import { useAppQuery } from "../../../http/useAppQuery.ts";
import { trackEvent } from "../../../tracking/tracking.ts";
import { UPLOAD_MAX_IMAGE_SIZE } from "../../constants.ts";
import { TOOL_TRACKING_NAMES } from "../display.ts";
import { useSelectedTool } from "../hooks/useSelectedTool.ts";
import { useUploadImage } from "../hooks/useUploadImage.ts";
import type { DetailedImage, GenerationType } from "../types.ts";
import { ImageDropZone } from "./ImageDropZone.tsx";

export const ImagePicker = ({
  imageUuid,
  onImageUuidChange,
  errors,
  pipelineCompatible = true,
}: {
  imageUuid?: string;
  onImageUuidChange: (uuid: string | undefined) => void;
  errors?: string[];
  pipelineCompatible?: boolean;
}) => {
  // FIXME: We need a way to make sure that the content is an image
  // not a video. We could either create a new route images/uuid
  // or add a query param to the current route.
  const { data: image } = useAppQuery<DetailedImage>({
    queryKey: imageUuid ? `contents/${imageUuid}` : null,
  });

  const [uploadingImage, setUploadingImage] = useState<{
    fileToUpload: File;
    uuid: string;
  }>();

  return (
    <ErrorWrapper errors={errors} className="w-full">
      <div
        className={classNames(
          "relative w-full flex-col-center min-h-[140px] rounded-150 border-input-border-width-active overflow-clip",
          errors && errors.length > 0
            ? "border-error-rest"
            : "border-input-border-active border-dashed ",
        )}
      >
        {uploadingImage === undefined && imageUuid === undefined ? (
          <ImageDropZone
            onDrop={setUploadingImage}
            minImageSideDimension={pipelineCompatible ? 256 : 0}
            content={
              <div className="w-full flex-col-center gap-200">
                <Icon name="ImagePlus" size="lg" />
                <div className="body-md-default">Drop image here or</div>
              </div>
            }
          />
        ) : (
          <div className="flex-row-center">
            {uploadingImage !== undefined ? (
              <InitialLocalImage
                file={uploadingImage.fileToUpload}
                onSuccess={(uuid) => {
                  onImageUuidChange(uuid);
                }}
                pipelineCompatible={pipelineCompatible}
              />
            ) : (
              <BaseImage
                className="h-full w-full flex-row-center"
                imageClassName="h-full max-w-full object-contain"
                src={image?.url ?? undefined}
              />
            )}
            {imageUuid && (
              <ClickableIcon
                name="X"
                size="sm"
                variant="primary"
                className="absolute right-150 top-150"
                onClick={() => {
                  onImageUuidChange(undefined);
                  setUploadingImage(undefined);
                }}
              />
            )}
          </div>
        )}
      </div>
    </ErrorWrapper>
  );
};

const InitialLocalImage = ({
  file,
  onSuccess,
  pipelineCompatible,
}: {
  file: File;
  onSuccess?: (image_uuid: string) => void;
  pipelineCompatible?: boolean;
}) => {
  const [src] = useState(() => URL.createObjectURL(file));
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const { mutate: uploadImage, isLoading: IsUploadImageLoading } =
    useUploadImage({
      describeImage: true,
      onSuccess: (imageUuid) => {
        onSuccess?.(imageUuid);
      },
      maxSize: UPLOAD_MAX_IMAGE_SIZE,
      pipelineCompatible,
    });

  const { selectedTool } = useSelectedTool();

  useOnMount(() => {
    uploadImage({ image: file as Blob });
    trackEvent(
      "content:upload",
      selectedTool
        ? {
            generation_type:
              TOOL_TRACKING_NAMES[selectedTool as GenerationType],
          }
        : {},
    );
  });

  return (
    <div className="h-full w-full relative flex-row-center">
      <BaseImage
        src={src}
        className="h-full w-full flex-row-center"
        imageClassName="h-full object-contain object-center"
        onLoad={() => setIsImageLoaded(true)}
      />
      {isImageLoaded && IsUploadImageLoading && (
        <Spinner className="absolute" />
      )}
    </div>
  );
};
